const isDev = import.meta.env.DEV;

/**
 * Normalize VITE_API_BASE_URL to .../api/v1 (host-only or /api paths accepted).
 * @param {string | undefined} base
 * @returns {string | null}
 */
const normalizeApiV1Url = (base) => {
  const trimmed = String(base || "").trim().replace(/\/$/, "");
  if (!trimmed) return null;
  if (trimmed.endsWith("/api/v1")) return trimmed;
  if (trimmed.endsWith("/api")) return `${trimmed}/v1`;
  return `${trimmed}/api/v1`;
};

/** When true, `npm run start:aws-beta` proxies /api to AWS beta (see vite.config.js). */
const useAwsBetaApi = import.meta.env.VITE_USE_AWS_BETA_API === "true";

const apiBaseOverride = normalizeApiV1Url(import.meta.env.VITE_API_BASE_URL);
const uiBaseOverride = String(import.meta.env.VITE_UI_BASE_URL || "")
  .trim()
  .replace(/\/$/, "");

/** Local dev via Vite proxy — same-origin cookies (toggle target in vite.config.js). */
const useDevProxy = import.meta.env.DEV && (useAwsBetaApi || !apiBaseOverride);

/**
 * Build-time or deploy-time stage (e.g. Vite `define` in CI).
 * Falls back to hostname for legacy hosts not yet setting VITE_ENV.
 */
const viteEnv = String(
  import.meta.env.VITE_ENV || import.meta.env.MODE || "development"
).toLowerCase();
const hostname =
  typeof window !== "undefined" ? window.location.hostname : "";

let API_STAGE = "dev";
if (isDev) {
  // Local dev: default "dev" cookies; use beta stage when proxied to AWS beta API.
  if (useAwsBetaApi || viteEnv === "beta") {
    API_STAGE = "beta";
  } else if (viteEnv === "alpha") {
    API_STAGE = "alpha";
  } else if (apiBaseOverride?.includes("beta.api.")) {
    API_STAGE = "beta";
  } else if (apiBaseOverride?.includes("alpha.api.")) {
    API_STAGE = "alpha";
  } else {
    API_STAGE = "dev";
  }
} else if (
  viteEnv === "beta" ||
  (hostname && hostname.split(".")[0] === "beta")
) {
  API_STAGE = "beta";
} else if (
  viteEnv === "alpha" ||
  (hostname && hostname.split(".")[0] === "alpha")
) {
  API_STAGE = "alpha";
} else {
  API_STAGE = "prod";
}

/*
  Stage
*/
export const STAGE = API_STAGE;

/*
  Auth Constants - Must match API constants/index.js
*/
export const TOKEN_NAME = "Attune-" + API_STAGE + "-Context";
export const REFRESH_TOKEN_NAME = "Attune-" + API_STAGE + "-Refresh";

export const PARENT_COMPANY = "Attune-" + API_STAGE + "-ParentCompany";
export const APP_TYPE = "Attune-" + API_STAGE + "-AppType";
export const USER = "Attune-" + API_STAGE + "-User";
/*
  Root API URL — override with VITE_API_BASE_URL in .env.local (e.g. AWS beta API).
*/
export const ROOT_API_URL = useDevProxy
  ? "/api/v1"
  : apiBaseOverride ||
    (isDev
      ? "http://localhost:8080/api/v1"
      : `https://${
          API_STAGE !== "prod" ? API_STAGE + "." : ""
        }api.attunelogic.com/api/v1`);

if (import.meta.env.DEV && typeof console !== "undefined") {
  console.info(
    "[attunelogic] API:",
    ROOT_API_URL,
    useAwsBetaApi ? "→ AWS beta (proxied)" : "→ local API (proxied)"
  );
}

/*
  Root UI URL
*/
export const ROOT_URL = uiBaseOverride
  ? `${uiBaseOverride}/`
  : isDev
    ? "http://localhost:3000"
    : `https://${
        API_STAGE !== "prod" ? API_STAGE + "." : ""
      }app.attunelogic.com/`;

/*
  Sentry dsn connection string
*/
export const SENTRY_DSN =
  "https://db40a7b27d08585b29086faf9766ef63@o4505003302780928.ingest.sentry.io/4506833066131456";

export const defaultSettings = {
  STAGE: API_STAGE,
  ROOT_API_URL,
  ROOT_URL,
  SENTRY_DSN,
  TOKEN_NAME,
  REFRESH_TOKEN_NAME,
};

export default { ...defaultSettings };
